import { Engine, Scene, Color3 } from '@babylonjs/core';

export class SceneManager {
  private engine: Engine;
  private scene: Scene;
  private canvas: HTMLCanvasElement;
  private resizeObserver?: ResizeObserver;
  private resizeListener?: () => void;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    
    // Create engine
    this.engine = new Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true
    });

    // Create scene
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color3(0.53, 0.81, 0.98).toColor4(); // Sky blue color
    
    // Attach input controls to canvas for keyboard/pointer events
    this.scene.attachControl();
  }

  getEngine(): Engine {
    return this.engine;
  }

  getScene(): Scene {
    return this.scene;
  }

  startRenderLoop(): void {
    this.engine.runRenderLoop(() => {
      this.scene.render();
    });
  }

  handleResize(): void {
    if (this.resizeListener) return;

    const resize = () => {
      this.engine.resize();
    };

    this.resizeListener = () => resize();
    window.addEventListener('resize', this.resizeListener, { passive: true });

    // Ensure correct initial sizing.
    resize();

    // More robust than relying on window resize alone (e.g., layout changes / overlays).
    if (typeof ResizeObserver !== 'undefined') {
      const target = this.canvas.parentElement ?? this.canvas;
      let rafId: number | null = null;

      this.resizeObserver = new ResizeObserver(() => {
        if (rafId != null) return;
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          resize();
        });
      });

      this.resizeObserver.observe(target);
    }
  }

  shutdown(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = undefined;
    }
    this.scene.dispose();
    this.engine.dispose();
  }
}
